import React, { FC, useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';

import LogoutButton from './component/LogoutButton';
import AdminTab from './AdminTab/AdminTab';
import ReviewTab from './ReviewTab/ReviewTab';
import ManualTab from './ManualTab/ManualTab';
import StatusTab from './StatusTab/StatusTab';
import TestingTab from './TestingTab/TestingTab';
import { getEmail, getLogin, getPermissions, getProviderId } from './service/permissionService';
import UserIcon from './component/UserIcon/UserIcon';
import EnvName from './component/EnvironmentName/EnvironmentName';
import setupRum from './service/DatadogService';
import { displayError } from './component/Message/Message';

import { EnvironmentConfig } from './model/EnvironmentConfig';
import UserPermissions, { DEFAULT_PERMISSIONS } from './model/userPermissions';

import version from './Version.json';

const App: FC<{ env: EnvironmentConfig }> = ({ env }) => {
  const {
    error, logout, isAuthenticated, getAccessTokenSilently,
  } = useAuth0();
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState<UserPermissions>(DEFAULT_PERMISSIONS);
  const [provider, setProvider] = useState('');
  const [accessToken, setAccessToken] = useState<string>('');
  const { t } = useTranslation();

  if (error || !isAuthenticated) {
    if (isAuthenticated) logout();
    return null;
  }

  useEffect(() => {
    getAccessTokenSilently().then(
      (fetchToken) => {
        const userLogin = getLogin(fetchToken);
        const userEmail = getEmail(fetchToken);
        setupRum(userLogin, userEmail, version.version, env.envName, env.datadogApplicationId, env.datadogClientToken)
        setLogin(userLogin);
        setEmail(getEmail(fetchToken));
        setProvider(getProviderId(fetchToken));
        setPermissions(getPermissions(fetchToken));
        setAccessToken(fetchToken);
      },
      (response: any) => {
        displayError(`Error occurred: ${response}`);
      },
    );
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: 'var(--u-font-family-2)',
        },
      }}
    >
      <div className="banner-info">
        <EnvName name={env.envName} version={version.version} domain={env.domainName} />
        &nbsp;
        &nbsp;
        |
        &nbsp;
        <UserIcon login={login} email={email} permissions={permissions.permissions} provider={provider} />
        &nbsp;
        |
        <LogoutButton />
      </div>
      <a href={env.umeUrl} target="_blank" rel="noreferrer">
        <img className="logo" src="assets/images/logos/UnderwriteMe_Logo.png" alt={`${t('logo.title')}`} title={`${t('logo.title')}`} />
      </a>
      <BrowserRouter>
        <nav className="tab-bar">
          {permissions.canOverride && (<NavLink to="/" >{t('tab.title.review')}</NavLink>)}
          {permissions.canOverride && (<NavLink to="/manual">{t('tab.title.manual')}</NavLink>)}
          <NavLink to="/status">{t('tab.title.status')}</NavLink>
          {(env.enableTestingTab && (permissions.canCreate || permissions.canAction)) && (<NavLink to="/testing">{t('tab.title.testing')}</NavLink>)}
          {(permissions.canOrgAdmin || permissions.canSystemAdmin) && (<NavLink to="/admin">{t('tab.title.admin')}</NavLink>)}
        </nav>
        <Routes>
          {permissions.canOverride && (
            <Route path="/" element={<ReviewTab accessToken={accessToken} />} />
          )}
          {permissions.canOverride && (
            <Route path="/manual" element={<ManualTab accessToken={accessToken} />} />
          )}
          <Route path="/status" element={<StatusTab permissions={permissions} accessToken={accessToken} />} />
          {(env.enableTestingTab && (permissions.canCreate || permissions.canAction)) && (
            <Route path="/testing" element={<TestingTab accessToken={accessToken} env={env} />} />
          )}
          {(permissions.canOrgAdmin || permissions.canSystemAdmin || permissions.canProviderSummary) && (
            <Route path="/admin" element={<AdminTab accessToken={accessToken} permissions={permissions} />} />
          )}
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default withAuthenticationRequired(App, {
});
